//crop-page.tsx

import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import { useEffect, useState, useContext, useRef } from "react";
import Title from "../components/title";
import { Link, useHistory, useParams } from "react-router-dom";
import { cropsApi } from "../services/api/crops";
import CropForm, {
  CropFormValues,
  convertToUnitSystem,
  otherUnitSystem,
} from "../components/crop-form";
import { Crop } from "../models/crop";
import { Box } from "@mui/material";
import LoadingBackdrop from "../components/loading-backdrop";
import { ErrorContext, UserContext } from "../components/app-routes";
import { seedsApi } from "../services/api/seeds";
import { Seed } from "../models/seed";
import { DateTime } from "luxon";
import mixpanel from "mixpanel-browser";
import { goBackOrTo } from "../utils/history";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ConfirmDialog from "../components/confirm-dialog";
import type { FormikHelpers } from "formik";
import StickyActionBar from "../components/sticky-action-bar";
import { Tray } from "../models/tray";
import { traysApi } from "../services/api/trays";
import { Grower } from "../models/grower";
import { getCustomLabel } from "../utils/custom-labels";

export default function CropPage() {
  const { id } = useParams<{ id: string }>();
  const formId = `crop-form-${id}`;
  const history = useHistory();
  const [busy, setBusy] = useState(false);
  const [crop, setCrop] = useState<Crop | null>(null);
  const [saved, setSaved] = useState(false);
  const { handleError } = useContext(ErrorContext);
  const [seeds, setSeeds] = useState<Seed[] | undefined>(undefined);
  const [trays, setTrays] = useState<Tray[] | null>(null);
  const [openChangeCropConfirmDialog, setOpenChangeCropConfirmDialog] =
    useState(false);
  const [initialValues, setInitialValues] = useState<
    CropFormValues | undefined
  >(undefined);
  const [submittedValues, setSubmittedValues] = useState<
    CropFormValues | undefined
  >(undefined);
  const helpersRef = useRef<FormikHelpers<CropFormValues> | null>(null);
  const context = useContext(UserContext);
  const grower = context.grower;
  if (!grower) {
    throw new Error("Invalid UserContext");
  }
  const isPro = grower.Plan === "pro";
  const isMounted = useRef(true);
  const [duplicateShortCodeError, setDuplicateShortCodeError] = useState(false);

  useEffect(() => {
    isMounted.current = true;

    const getCrop = async () => {
      setBusy(true);

      try {
        const [crop, seeds, trays] = await Promise.all([
          cropsApi.getSingle(parseInt(id)),
          seedsApi.getAll(),
          isPro ? traysApi.getAll() : Promise.resolve([]),
        ]);

        if (!isMounted.current) return;

        setCrop(crop);
        setSeeds(seeds);
        setTrays(trays);
        setInitialValues({
          id: crop.id,
          name: crop.name,
          shortCode: crop.shortCode,
          seedId: crop.seedId,
          trayId: crop.trayId ?? "",
          seedingRate: crop.seedingRate.toString(),
          seedingRateOther: convertToUnitSystem(
            otherUnitSystem(grower!.UnitSystem),
            crop.seedingRate,
          ).toFixed(2),
          overnightSoak: crop.overnightSoak,
          soakTimeMinutes: crop.soakTimeMinutes.toString(),
          germinationDays: crop.germinationDays.toString(),
          blackoutDays: crop.blackoutDays.toString(),
          daysToMaturity: crop.daysToMaturity.toString(),
          cutYield: crop.cutYield?.toString(),
          notes: crop.notes,
          archived: !!crop.archivedAt,
          bufferPercentage: crop.bufferPercentage.toString(),
        });
      } catch (error) {
        if (isMounted.current) handleError(error);
      } finally {
        if (isMounted.current) setBusy(false);
      }
    };

    getCrop();

    return () => {
      isMounted.current = false; // cleanup
    };
  }, [id, grower, handleError]);

  const updateCrop = async (
    values: CropFormValues,
    helpers: FormikHelpers<CropFormValues>,
  ) => {
    if (!crop || !initialValues) {
      throw new Error("Invalid state");
    }

    setBusy(true);

    try {
      await cropsApi.update({
        id: crop.id,
        name: values.name,
        shortCode: values.shortCode,
        seedId: values.seedId === "" ? 0 : Number(values.seedId),
        trayId: values.trayId === "" ? null : Number(values.trayId),
        seedingRate: parseFloat(values.seedingRate),
        overnightSoak: values.overnightSoak,
        soakTimeMinutes: parseInt(values.soakTimeMinutes),
        germinationDays: parseInt(values.germinationDays),
        blackoutDays: parseInt(values.blackoutDays),
        daysToMaturity: parseInt(values.daysToMaturity),
        cutYield: values.cutYield ? parseFloat(values.cutYield) : undefined,
        notes: values.notes,
        archivedAt:
          !!crop.archivedAt === values.archived
            ? crop.archivedAt
            : values.archived
              ? DateTime.now().toISO()
              : null,
        bufferPercentage: parseFloat(values.bufferPercentage),
      } as any);

      setSaved(true);
      helpers.resetForm({ values });

      mixpanel.track("Update Crop", {
        bufferPercentage: parseFloat(values.bufferPercentage),
      });

      setTimeout(() => {
        goBackOrTo(history, "/home/crops");
      }, 0);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        helpers.setFieldError("shortCode", "This short code is already in use");
        setDuplicateShortCodeError(true);
        return;
      }

      handleError(error);
    } finally {
      setBusy(false);
      helpers.setSubmitting(false);
    }
  };

  const handleSubmit = async (
    values: CropFormValues,
    helpers: FormikHelpers<CropFormValues>,
  ) => {
    if (!initialValues) {
      throw new Error("Invalid state: initialValues not loaded");
    }

    // If key scheduling fields changed, show warning dialog first
    if (
      initialValues.blackoutDays !== values.blackoutDays ||
      initialValues.daysToMaturity !== values.daysToMaturity ||
      initialValues.germinationDays !== values.germinationDays ||
      initialValues.overnightSoak !== values.overnightSoak
    ) {
      setSubmittedValues(values);
      helpersRef.current = helpers;
      setOpenChangeCropConfirmDialog(true);
      return;
    }

    await updateCrop(values, helpers);
  };

  const onClickCancel = () => {
    goBackOrTo(history, "/home/crops");
  };

  const onClickCloseChangeCropConfirmDialog = (
    confirm: boolean | undefined,
  ) => {
    setOpenChangeCropConfirmDialog(false);

    if (!confirm) {
      return;
    }

    if (!submittedValues || !helpersRef.current) {
      throw new Error("Invalid state");
    }

    updateCrop(submittedValues, helpersRef.current);
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
      <Box>
        <Paper
          sx={{
            p: 2,
            display: "flex",
            overflow: "visible",
            flexDirection: "column",
          }}
        >
          <Title>{getCustomLabel(grower ?? null, "crops")}</Title>{" "}
          {crop && seeds && trays && initialValues && (
            <>
              <div>
                <Button
                  sx={{ mb: 2 }}
                  variant="contained"
                  color="primary"
                  component={Link}
                  to={{
                    pathname: "/home/crops/add",
                    state: {
                      crop: {
                        ...crop,
                        name: `${crop.name} (COPY)`,
                        shortCode: "",
                      },
                    },
                  }}
                  onClick={() => mixpanel.track("Duplicate Crop")}
                  disabled={busy}
                  startIcon={<ContentCopyIcon />}
                >
                  Duplicate
                </Button>
              </div>

              <CropForm
                formId={formId}
                initialValues={initialValues}
                seeds={seeds}
                trays={trays}
                onSubmit={handleSubmit}
                saved={saved}
                grower={grower!}
              >
                {crop && seeds && initialValues && (
                  <StickyActionBar position="bottom">
                    <Button
                      variant="outlined"
                      disabled={busy}
                      onClick={onClickCancel}
                    >
                      Cancel
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      form={formId} // ✅ key: submits the form even though button is outside it
                      disabled={busy}
                    >
                      Save
                    </Button>
                  </StickyActionBar>
                )}
              </CropForm>
            </>
          )}
        </Paper>
      </Box>
      <LoadingBackdrop open={busy} />
      <ConfirmDialog
        title="Warning"
        description={
          <div>
            Changing a crop&apos;s values could affect Tasks that have already
            been completed, including shifting Tasks from one day to another.
            <br />
            <br />
            Unsure what to do?{" "}
            <a
              href="https://seedleaf.freshdesk.com/a/solutions/articles/72000625968"
              target="_blank"
              rel="noreferrer"
            >
              Read more here
            </a>
            .
            <br />
            <br />
            Would you like to proceed?
          </div>
        }
        onClose={onClickCloseChangeCropConfirmDialog}
        open={openChangeCropConfirmDialog}
      />
      <ConfirmDialog
        title="Duplicate Short Code"
        description="This crop short code already exists — please choose another."
        open={duplicateShortCodeError}
        onClose={() => {
          setDuplicateShortCodeError(false);

          // Scroll into view & focus on shortCode field
          const el = document.querySelector(
            'input[name="shortCode"]',
          ) as HTMLInputElement | null;
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            setTimeout(() => el.focus(), 300);
          }
        }}
      />
    </Container>
  );
}
